import type { Device } from "../../store/admin/devicesStore";

export const DEVICE_KINDS = ["general", "camera", "phone"] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

export const DEFAULT_DEVICE_KIND: DeviceKind = "general";

export const DEVICE_KIND_LABEL_KEYS = {
  general: "devices.kinds.general",
  camera: "devices.kinds.camera",
  phone: "devices.kinds.phone",
} as const satisfies Record<DeviceKind, string>;

type PairingFields = Pick<Device, "parent_device_id" | "paired_with">;

export const deviceKindOf = (device: Pick<Device, "device_kind">): DeviceKind =>
  device.device_kind ?? DEFAULT_DEVICE_KIND;

export const parentDeviceIdOf = (device: PairingFields): string | null =>
  device.parent_device_id || device.paired_with || null;

export const isSecondaryDevice = (device: PairingFields): boolean =>
  parentDeviceIdOf(device) !== null;

export const parentIdsOf = (devices: PairingFields[]): Set<string> =>
  new Set(devices.map(parentDeviceIdOf).filter((id): id is string => id !== null));

export const hasSecondaryDevices = (devices: PairingFields[], deviceId: string): boolean =>
  parentIdsOf(devices).has(deviceId);

const secondaryRank = (device: Pick<Device, "priority"> & PairingFields): number =>
  isSecondaryDevice(device) ? Number(device.priority) || Number.MAX_SAFE_INTEGER : 0;

export const sortUnitDevices = <T extends Pick<Device, "id" | "priority"> & PairingFields>(
  devices: T[],
): T[] =>
  [...devices].sort(
    (a, b) => secondaryRank(a) - secondaryRank(b) || a.id.localeCompare(b.id),
  );
